import { useMemo } from "react";

import useTypedContext from "hooks/useTypedContext";
import { EmptystateLinkColored, EmptystateMagnetColored } from "components/icons/generated";
import EmptyState from "ds/components/EmptyState";
import LoadingIndicator from "ds/components/LoadingIndicator";
import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import Counter from "ds/components/Counter";
import ContextAttachment from "components/ContextAttachment";
import MissingDataBanner from "components/MissingDataBanner";

import { StackFormContext } from "../context";
import { StackCreationWizardStep } from "../types";
import useAttachedContexts from "../AttachContext/useAttachedContexts";
import NewStackSummaryPanel from "./Panel";

type NewStackSummaryAttachedContextsProps = {
  title: string;
  goToStepDisabled?: boolean;
};

const NewStackSummaryAttachedContexts = ({
  title,
  goToStepDisabled,
}: NewStackSummaryAttachedContextsProps) => {
  const { createdStackId, formData } = useTypedContext(StackFormContext);
  const stackLabels = useMemo(
    () => formData[StackCreationWizardStep.Details].labels.map((item) => item.value),
    [formData]
  );

  const {
    allContexts,
    autoAttachedContexts,
    manuallyAttachedContexts,
    hasData,
    loading,
    refetch,
    refetching,
  } = useAttachedContexts(createdStackId);

  return (
    <>
      <NewStackSummaryPanel
        name={title}
        count={allContexts.length}
        step={StackCreationWizardStep.AttachContext}
        goToStepDisabled={goToStepDisabled}
      >
        {!loading && hasData && (
          <Box direction="column" gap="x-large">
            <Box direction="column" gap="medium">
              <Box grow="1" align="center" gap="medium">
                <Typography tag="h4" variant="p-t7">
                  Attached directly
                </Typography>
                <Counter count={manuallyAttachedContexts.length} size="medium" />
              </Box>
              <Box direction="column" gap="medium">
                {manuallyAttachedContexts.length > 0 ? (
                  manuallyAttachedContexts.map((context) => (
                    <ContextAttachment key={context.id} context={context} readonly={true} />
                  ))
                ) : (
                  <EmptyState
                    padding="large"
                    icon={EmptystateLinkColored}
                    title="No contexts are attached to this stack"
                    caption="Use contexts list to attach a context to stack."
                  />
                )}
              </Box>
            </Box>
            <Box direction="column" gap="medium">
              <Box grow="1" align="center" gap="medium">
                <Typography tag="h4" variant="p-t7">
                  Auto-attached
                </Typography>
                <Counter count={autoAttachedContexts.length} />
              </Box>
              <Box direction="column" gap="medium">
                {autoAttachedContexts.length > 0 ? (
                  autoAttachedContexts.map((context) => (
                    <ContextAttachment
                      key={context.id}
                      context={context}
                      stackLabels={stackLabels}
                      readonly
                    />
                  ))
                ) : (
                  <EmptyState
                    padding="large"
                    icon={EmptystateMagnetColored}
                    title="No contexts are auto-attached to this stack"
                    caption="Use auto-attach label to attach context automatically."
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}

        {allContexts.length === 0 && loading && (
          <Box padding="large" justify="center">
            <LoadingIndicator size="x-large" loading />
          </Box>
        )}

        {allContexts.length === 0 && !hasData && (
          <MissingDataBanner
            text="Couldn't load attached contexts, please refresh or come back later"
            refreshHandler={refetch}
            refreshLoading={refetching}
          />
        )}
      </NewStackSummaryPanel>
    </>
  );
};

export default NewStackSummaryAttachedContexts;
